import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePropertiesList } from "../properties/api.js";
import {
  serviceCategoryLabels,
  urgencyLabels,
  useRfqImageUrl,
  useRfqsList,
  type Rfq,
  type RfqStatus,
} from "../rfqs/api.js";
import { ButtonLink } from "../ui/Button.js";
import { EmptyState } from "../ui/EmptyState.js";
import { ChevronRightIcon, PlusIcon, WrenchIcon } from "../ui/icons.js";
import { SkeletonCard } from "../ui/Skeleton.js";
import { StatusBadge, statusLabel } from "../ui/StatusBadge.js";

type FilterValue = "ALL" | RfqStatus;

const filters: { value: FilterValue; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "IN_REVIEW", label: "In review" },
  { value: "CLOSED", label: "Closed" },
  { value: "CANCELLED", label: "Cancelled" },
];

export function AppRfqs() {
  const [filter, setFilter] = useState<FilterValue>("ALL");
  const { data: rfqs, isLoading, error } = useRfqsList(filter === "ALL" ? {} : { status: filter });
  const propertiesQuery = usePropertiesList();
  const propertyNickname = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of propertiesQuery.data ?? []) map.set(p.id, p.nickname);
    return map;
  }, [propertiesQuery.data]);

  return (
    <div className="mx-auto max-w-md space-y-5 p-4 pt-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Your requests</h1>
        <ButtonLink to="/app/rfqs/new" size="sm" className="rounded-full">
          <PlusIcon className="h-4 w-4" />
          New
        </ButtonLink>
      </header>

      <div
        role="tablist"
        aria-label="Filter by status"
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
      >
        {filters.map((f) => {
          const active = f.value === filter;
          return (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(f.value)}
              className={`press min-h-[36px] shrink-0 rounded-full border px-3.5 text-sm font-medium ${
                active
                  ? "border-brand-700 bg-brand-700 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <ul className="space-y-3" aria-hidden>
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i}>
              <SkeletonCard />
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          Couldn&apos;t load requests. {error instanceof Error ? error.message : ""}
        </p>
      )}

      {rfqs && rfqs.length === 0 && !isLoading && <RfqsEmpty filter={filter} />}

      {rfqs && rfqs.length > 0 && (
        <ul className="space-y-3">
          {rfqs.map((r) => (
            <li key={r.id}>
              <RfqCard rfq={r} propertyName={propertyNickname.get(r.propertyId)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RfqCard({ rfq, propertyName }: { rfq: Rfq; propertyName: string | undefined }) {
  const firstImage = rfq.images?.[0];
  return (
    <Link
      to={`/app/rfqs/${rfq.id}`}
      className="press flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-50"
    >
      <Thumbnail rfqId={rfq.id} imageId={firstImage?.id} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-base font-semibold text-slate-900">
            {serviceCategoryLabels[rfq.serviceCategory]}
          </p>
          <StatusBadge status={rfq.status} />
        </div>
        <p className="mt-1 truncate text-sm text-slate-600">{urgencyLabels[rfq.urgency]}</p>
        <p className="mt-2 truncate text-xs text-slate-500">
          {propertyName ? `${propertyName} · ` : ""}
          {new Date(rfq.createdAt).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </p>
      </div>
      <ChevronRightIcon className="mt-1 h-5 w-5 shrink-0 text-slate-300" />
    </Link>
  );
}

function Thumbnail({ rfqId, imageId }: { rfqId: string; imageId: string | undefined }) {
  // Always call the hook; pass undefined when there's no image so the query
  // stays disabled (keeps hook order stable across renders).
  const { data } = useRfqImageUrl(rfqId, imageId);
  if (!imageId) {
    return (
      <div
        aria-hidden
        className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-400"
      >
        <WrenchIcon className="h-6 w-6" />
      </div>
    );
  }
  if (!data) {
    return <div className="skeleton h-16 w-16 shrink-0 rounded-xl" />;
  }
  return <img src={data.url} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />;
}

function RfqsEmpty({ filter }: { filter: FilterValue }) {
  if (filter !== "ALL") {
    return (
      <EmptyState
        title={`No ${statusLabel(filter).toLowerCase()} requests`}
        description="When you have requests in this status, they'll show up here."
      />
    );
  }
  return (
    <EmptyState
      icon={<WrenchIcon className="h-7 w-7" />}
      title="Start your first request"
      description="We'll help you find the right person for the job."
      action={
        <ButtonLink to="/app/rfqs/new" size="lg" fullWidth>
          New request
        </ButtonLink>
      }
    />
  );
}
